import React, { useContext, useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
    Box,
    IconButton,
    LinearProgress,
    useTheme,
    useMediaQuery,
    Tooltip
} from '@mui/material';
import { linearProgressClasses } from '@mui/material/LinearProgress'
import {
    DataGridPro,
    gridClasses,
    LicenseInfo,
    useGridApiRef 
} from '@mui/x-data-grid-pro';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import ProjectContext from '../contexts/ProjectContext';
import UserContext from '../contexts/UserContext';

import ExploreViewMenuBar from '../components/menubar/ExploreViewMenuBar';
import ExportCdlDialog from '../components/dialog/ExportCdlDialog';
import SelectExportsDialog from '../components/dialog/SelectExportsDialog';
import SelectEdlDialog from '../components/dialog/SelectEdlDialog';
import ShotDetailPage from './ShotDetailPage/ShotDetailPage';
import UploadExploreDialog from '../components/dialog/UploadExploreDialog';
import UploadExploreMobile from '../components/dialog/UploadExploreMobile';
import UploadEDLForCDLInjectDialog from '../components/dialog/UploadEDLForCDLInjectDialog';
import {useFetch} from '../components/auth/useFetch';
import { ContentPasteSearchOutlined } from '@mui/icons-material';

LicenseInfo.setLicenseKey('17ca2e8476fa658618680f9f135dbc81T1JERVI6MzU2MTYsRVhQSVJZPTE4NzMzODQyMDIwMDAsS0VZVkVSU0lPTj0x',);

const GreenCheck = () => {
    return(
        <CheckIcon style={{ color:'#19f745' }} />
    )
}
const RedCross = () => {
    return(
        <CloseIcon style={{ color:'#f72019'}} />
    )
}

export default function ExploreView(props) {
    // Theme for breakpoints
    const theme = useTheme();

    // State stuff
    const apiRef = useGridApiRef()
    const { transferdate, makeTour, isLoading, setIsLoading } = props
    const { isAuthenticated } = useAuth0();
    const projectContext = useContext(ProjectContext);
    const { konsolUser, setKonsolUser } = useContext(UserContext)
    const fetchWithAuth = useFetch()

    const detailsColumn = [
        { field: 'details',     headerName: '', renderCell: (params) => 
            <Tooltip title="Open Shot Details" placement="top" arrow>
                <IconButton
                    variant="contained"
                    color="primary"
                    onClick={(event) => {
                        handleDetail(event, params.row.ClipName);
                    }}>
                    <OpenInNewIcon />
                </IconButton>
            </Tooltip>
        , width: 120 }
    ]

    const [mediaColumns, setMediaColumns] = useState([
        { field: 'thumbnail', headerName: 'Thumbnail', disableExport: true, renderCell: (params) =>
            <Box
                width="100vw"
            >
                <img
                    alt="thumbnail"
                    width="180px"
                    src={`data:image/png;base64, ${params.row.thumbnail}`}
                    onError={e => e.currentTarget.src = '/posters/fallbackThumbnail.png'}
                />
            </Box>
        , width: 220}
    ])
    const [metadataSourceColumns, setMetadataSourceColumns] = useState([])

    const [tabs, setTabs] = useState(['Explore'])
    const [value, setValue] = React.useState(0);
    const [exploreColumns, setExploreColumns] = useState([...detailsColumn, ...mediaColumns, ...metadataSourceColumns])
    const [exploreRows, setExploreRows] = useState([])
    const [loadProgress, setLoadProgress] = useState(0)
    const [cdlSource, setCdlSource] = useState('Colorfront')
    const [cdlFormat, setCdlFormat] = useState('cdl_individual')


    const [rowSelectionModel, setRowSelectionModel] = useState([])
    const [filterModel, setFilterModel] = useState({ items: [], })
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(konsolUser?.user_metadata?.columns ?? {})

    const [showSelectedOnly, setShowSelectedOnly] = useState(false)


    // const [openUploadDialog, setOpenUploadDialog] = useState(false)
    const [openExportMenu, setOpenExportMenu] = useState(false)
    const [openEdlMenu, setOpenEdlMenu] = useState(false)
    const [openCdlMenu, setOpenCdlMenu] = useState(false)
    const [openCdlUploadMenu, setOpenCdlUploadMenu] = useState(false)
    const [cdlUploadSource, setCdlUploadSource] = useState('Colorfront')


    // Control bar variables
    const [searchText, setSearchText] = useState('')
    const [openUploadDialog, setOpenUploadDialog] = useState(false)

    const [uploadRef, setUploadRef] = useState(null)
    const [exportAnchorEl, setExportAnchorEl] = useState(null)
    const [edlAnchorEl, setEdlAnchorEl] = useState(null)
    const [CdlAnchorEl, setCdlAnchorEl] = useState(null)
    const [CdlUploadAnchorEl, setCdlUploadAnchorEl] = useState(null)

    useEffect(() => {
        const existingColumns = new Set([...metadataSourceColumns.map(col => col.field), ...detailsColumn.map(col => col.field)]);
        const filteredMediaColumns = mediaColumns.filter(col => !existingColumns.has(col.field))
        setExploreColumns([ ...detailsColumn, ...filteredMediaColumns, ...metadataSourceColumns])
    }, [mediaColumns, metadataSourceColumns])

    useEffect(() => {
        makeTour([
            {
                title: 'Explore your Project',
                target: '#explore-view-table',
                content: 'View all the metadata across all transfer dates. Double click on a row to open a new tab which has all the metadata including associated frame-by-frame metadata.',
                placementBeacon: 'top'
            },
            {
                title: 'Explore your Project',
                target: '#explore-view-search-bar',
                content: 'Use the search bar to filter the shots by a keyword that is searched across all associated metadata.',
            },
            // {
            //     title: 'Explore your Project',
            //     target: '#explore-view-edl-filter',
            //     content: 'Upload an EDL, or select an existing EDL to view the shots found in the EDL.',
            // },
            {
                title: 'Explore your Project',
                target: '#explore-view-shots-filter',
                content: 'Toggle between all shots and selected shots.',
            },
            // {
            //     title: 'Explore your Project',
            //     target: '#explore-view-generate-export',
            //     content: 'Export the selected shots using a configured export.',
            // },
            {
                title: 'Explore your Project',
                target: '#explore-view-generate-csv',
                content: 'Export the selected shots as a CSV.',
            },
            {
                title: 'Explore your Project',
                target: '#explore-view-generate-edl',
                content: 'Generate an EDL using the selected shots.',
            },
            {
                title: 'Explore your Project',
                target: '#explore-view-generate-cdl',
                content: 'Generate a CCC file for a single selected shot.',
            },
        ])
    }, [])


    useEffect(() => {
        isAuthenticated && (() => {
            updateColumnSources()
            retrieveShots()
        })()
    }, [isAuthenticated])


    const updateColumnSources = () => {
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/sources`, {
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then((res) => res.json())
            .then(result => {
                const sources = Object.keys(result).map(source => {
                    return {field: source, headerName: source, width: 150, type: 'boolean'}
                })
                setMetadataSourceColumns(sources)
            })
            .catch(e => {
                console.error(e);
            })
    } 

    const retrieveShots = (data) => {
        setIsLoading(true)
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/explore/?page=${data?.page ?? 1}&size=${data?.page_size ?? 250}`, {
            headers: {
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then((res) => res.json())
            .then(result => {
                setExploreRows(prev => prev.concat(result?.data ?? []))
                setLoadProgress(calculateProgress(result?.metadata?.page, result?.metadata?.page_count))

                // Code for dynamic generation of Datagrid columns

                setMediaColumns((prev) => {
                    const existingColumns = new Set(prev.map(col => col.field));
                    const uniqueColumns = generateDataGridColumns(result.data).filter(col => !existingColumns.has(col.field));
                    
                    return [...prev, ...uniqueColumns]
                })
                if(result?.metadata && result.metadata?.page && result.metadata?.page_count && result.metadata.page < result.metadata.page_count ) {
                    result.metadata.page += 1
                    retrieveShots(result.metadata)
                }
                else{
                    setIsLoading(false)
                    setLoadProgress(0)
                }
            })
            .catch(e => {
                console.error(e);
            })
    }


    // Code for dynamic generation of Datagrid columns

    const generateDataGridColumns = (rows) => {
        const resultMap = new Map()
        for(const row of rows){
            for(const key in row){
                if(!resultMap.has(key)){
                    resultMap.set(key, typeof row[key])
                }
            }
        }
        return Array.from(resultMap, ([field, type]) => ({ field, type, width: 200 }));
    }

    const handleDetail = (event, clipname) => {
        const newArray = [...tabs, clipname];
        if (!tabs.includes(clipname)) {
            setTabs(newArray);
        }
        const index = newArray.indexOf(clipname)
        setValue(index)
    }

    const handleColumnVisibilityChange = (visibilityModel) => {
        setIsLoading(true)
        setColumnVisibilityModel(visibilityModel)
    }

    // Debounce the request by waiting 5 seconds before updating setting
    // useEffect(() => {
    //     const updateData = setTimeout(() => {
    //         const user_metadata = konsolUser?.user_metadata ?? {}
    //         user_metadata.columns = columnVisibilityModel
            
    //         var raw = JSON.stringify(user_metadata);
    //         var requestOptions = {
    //             method: 'PATCH',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'X-Project-ID': projectContext?.project_db,
    //             },
    //             body: raw,
    //             redirect: 'follow',
    //         };
    //         fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/settings/`, requestOptions)
    //             .then(response => {
    //                 if(response.status === 200){
    //                     setKonsolUser((prev) => ({ ...prev, user_metadata }))
    //                 }
    //             })
    //             .catch(e => {
    //                 console.error(e);
    //             })
    //             .finally(() => setIsLoading(false))
    //     }, 5000)
    //     return () => clearTimeout(updateData)
    // }, [columnVisibilityModel])


    /////////////////////////////////
    // MenuBar Functions ///////////
    // Search Functions

    // TODO: What's the purpose of this function?
    // Assumption - intended to call this function in the requestSearch
    // Easy to implement if true
    function escapeRegExp(value) { 
        return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&") 
    }

    // Handle chars typed into search field
    const requestSearch = (searchValue) => {
        const search = searchValue.target.value
        setSearchText(search)
        setFilterModel(prev => {
            if(search !== ''){
                prev['quickFilterValues'] = [search]
            }
            else{
                delete prev.quickFilterValues
            }
            return prev
        })
    }
    // Clear search field when X clicked
    const clearSearch = () => {
        setSearchText('')
        setFilterModel(prev => {
            delete prev.quickFilterValues
            return prev
        })
    }

    // Clear all filters applied to shots data grid, incl. search text box
    const clearFilter = () => {
        setSearchText('')
        setFilterModel({
            items: [
            ],
        })
    }

    // TODO: what does?
    const toggleSelectedRows = () => {
        setShowSelectedOnly(prev => !prev)
    }


    // Show/hide Export menu list
    const toggleExportMenu = (e) => {
        setOpenExportMenu((prev) => !prev)
        setExportAnchorEl(e.currentTarget)
    }

    // Show/hide EDL menu list
    const toggleEdlMenu = (e) => {
        setOpenEdlMenu((prev) => !prev)
        setEdlAnchorEl(e.currentTarget)
    }

    // Show/hide EDL menu list
    const toggleCdlMenu = (e) => {
        setOpenCdlMenu((prev) => !prev)
        setCdlAnchorEl(e.currentTarget)
    }
    // Show/hide EDL menu list
    const toggleCdlUploadMenu = (e) => {
        setOpenCdlUploadMenu((prev) => !prev)
    }    

    // Form EDL download request params
    const generateEDL = () => {   
        var myHeaders = new Headers()       
        myHeaders.append("Content-Type", "application/json")
        myHeaders.append('X-Project-ID', projectContext?.project_db)
        let clipnames = getSelectedClipNames()
        
        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify({"clipnames": clipnames}),
            redirect: 'follow'
        }
        downloadEDL(requestOptions)
    }

    // Form EDL with CDL data download request params
    const generateCDLEDL = (cdl) => {  
        var myHeaders = new Headers()       
        myHeaders.append("Content-Type", "application/json")
        myHeaders.append('X-Project-ID', projectContext?.project_db)
        let clipnames = getSelectedClipNames()        
        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify({"clipnames": clipnames, "cdl_options": cdl}),
            redirect: 'follow'
        }
        downloadEDL(requestOptions)
    }

    // Generate and download EDL
    const downloadEDL = (requestOptions) => {
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/generateedl/`, requestOptions)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'custom_edl.edl');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            })
            .catch(e => {
                console.error(e);
            })
    }



    // Generate and download CDL
    async function downloadCDL(item) {
        try {
            const clipNames = getSelectedClipNames()
            let body = {source: item['source'], format: item['type'], clips: clipNames}

            var myHeaders = new Headers()               
            myHeaders.append("Content-Type", "application/json")
            myHeaders.append('X-Project-ID', projectContext?.project_db)
            var requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: JSON.stringify(body),
                redirect: 'follow'
            }
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/cdl/`, requestOptions)
            
            // Get filename from content-disposition header without consuming response
            let content_disposition = response.headers.get('content-disposition')
            const regex = /(?<=filename=").+(?=")/g
            const filename = content_disposition.match(regex)[0]
    
            // Consume response
            const responseBlob = await response.blob()
            const url = window.URL.createObjectURL(new Blob([responseBlob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

        }
        catch(e){
            console.error(e);
        }
    }





    // Generate and download CSV Export
    const downloadCSV = () => {
        apiRef.current.exportDataAsCsv()
    }



    // Calculate progress for determinate loading bar
    const calculateProgress = (current, total) => {
        if(current > total || total === 0){
            total = current
        }
        return current/total * 100
    }

    // Render determinite loading bar
    function determinateLoadingOverlay() {
        return (
            <LinearProgress
                variant='determinate'
                value={loadProgress}
                sx={{
                    [`&.${linearProgressClasses.determinate}`]: {
                        backgroundColor: 'rgb(48, 89, 69)',
                    },
                    [`& .${linearProgressClasses.bar}`]: {
                        backgroundColor: 'rgb(178, 235, 177)',
                    },
                }}
            />
        )
    }


    const downloadExport = (item) => {
        const clipNames = getSelectedClipNames()
        const payload = {'clips': clipNames}
        let url
        if(item?.type === 'ALE'){
            url = `${process.env.REACT_APP_BACKEND_URL}/konsol/ale/?output_type=ale&output_format=ale&ale_name=${item?.name}`
        }
        else if(item?.type === 'PDF'){
            url = `${process.env.REACT_APP_BACKEND_URL}/konsol/generatepdf/?output_type=pdf&output_format=pdf&pdf_name=${item?.name}`
        }
        else if(item?.type === 'CSV'){
            url = `${process.env.REACT_APP_BACKEND_URL}/konsol/ale/?output_type=csv&output_format=csv&ale_name=${item?.name}`            
        }
        fetchWithAuth(url, {
            method: 'POST',
            headers: {
                'X-Project-ID': projectContext?.project_db,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload),
        })
            .then((response) => response.blob())
            .then((res) => {
                const url = window.URL.createObjectURL(res);
                const link = document.createElement('a');
                link.href = url;
                let ext
                if(item?.type === 'ALE'){
                    ext = '.ALE'
                }
                else if(item?.type === 'PDF'){
                    ext = '.PDF'
                }
                else if(item?.type === 'CSV'){
                    ext = '.CSV'
                }
               const filename = `${transferdate}-${item?.name}${ext}`
               link.setAttribute('download', filename);
               document.body.appendChild(link);
               link.click();
               link.parentNode.removeChild(link);
            })
            .catch(e => {
                console.error(e);
            })
    }


    const fileDialog = (e) => {
        setUploadRef(e.currentTarget)
        setOpenUploadDialog((prevState) => !prevState)
    }


    // Filter clips shown in datagrid
    // Used by child components, usually upload modals
    const filterClips = (clipNames) => {
        setShowSelectedOnly(true)
        const uniqueClipNames = [...new Set(clipNames)]
        setRowSelectionModel((prev) => {
            const newIds = exploreRows
            .filter((shot) => uniqueClipNames.includes(shot.ClipName))
            .map(shot => shot.UniqueID);
            // Combine previous and new IDs, then filter for uniqueness
            return Array.from(new Set([...prev, ...newIds]));
        })
        // FIXME
        // Setting this on the selection model does not check the box
    }

    const getSelectedClipNames = () => {
        let filtered = exploreRows.filter((shot) => rowSelectionModel.includes(shot.UniqueID))
        return exploreRows
            .filter((shot) => rowSelectionModel.includes(shot.UniqueID))
            .map((shot) => shot.ClipName)
    }


    return (
        <React.Fragment>
            <Box display="flex" flexDirection="column" height='100%'>
                <ExploreViewMenuBar
                    requestSearch={requestSearch}
                    clearSearch={clearSearch}
                    clearFilter={clearFilter}
                    searchText={searchText}
                    toggleSelectedRows={toggleSelectedRows}
                    setOpenUploadDialog={setOpenUploadDialog}
                    generateEDL={generateEDL}
                    generateCDLEDL={generateCDLEDL}
                    downloadCSV={downloadCSV}
                    downloadCDL={downloadCDL}
                    downloadEDL={downloadEDL}
                    toggleEdlMenu={toggleEdlMenu}
                    toggleCdlMenu={toggleCdlMenu}
                    toggleExportMenu={toggleExportMenu}
                    selectionModel={rowSelectionModel}
                    setUploadRef={setUploadRef}
                    tabs={tabs}
                    setTabs={setTabs}
                    value={value}
                    setValue={setValue}
                    filterClips={filterClips}
                />
    
                
                <Box
                    display="flex"
                    flexDirection="row"
                    height='calc(100% - 47px)'
                    width='100%'
                >
                {value === 0 && (
                    <div id='explore-view-table' style={{ width: '100%', height: '100%' }}>
                    <DataGridPro
                        sx={{
                            bgcolor: "rgba(0,0,0,0.9)",
                            boxShadow: 2,
                            border: 0,
                            '& .MuiSvgIcon-root': {
                                color: "rgb(135, 135, 135)"
                            },
                            // Transition reduces split second switch to non-selected style on mouseover change
                            '& .MuiDataGrid-row':{
                                transition: '0.1s'
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: "1px solid rgba(224, 224, 224, 0.8)"
                            },
                            '& .MuiDataGrid-overlay': {
                                backgroundColor: "rgba(255, 255, 255, 0.01)"
                            },
                            '& .MuiDataGrid-row.Mui-selected': {
                                backgroundColor: "rgba(36, 108, 164, 0.35)",
                            },
                            '& .MuiDataGrid-row.Mui-selected:hover': {
                                backgroundColor: "rgba(36, 108, 164, 0.35)",
                             },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                color: "rgb(255, 255, 255)",
                                fontWeight: "bold",
                            },
                            '& .MuiDataGrid-columnHeadersInner': {
                                backgroundColor: "rgba(0, 0,0 , 0.1)",
                            },
                            '& .MuiDataGrid-pinnedColumns': {
                                backgroundColor: "#1c2025",
                                backgroundImage: 'none',

                                boxShadow: '0px 3px 1px -2px rgb(0 0 0 / 50%), 0px 2px 2px 0px rgb(0 0 0 / 40%), 0px 1px 5px 0px rgb(36 108 164 / 90%)',
                            },
                            '& .MuiDataGrid-pinnedColumnHeaders': {
                                backgroundColor: "rgb(40,44,52)",
                                backgroundImage: 'none',
                                boxShadow: 'none',
                                borderColor: "rgba(77, 78, 82, 0.7)",
                            },
                            '& .MuiDataGrid-cell': {
                                color: "rgb(255, 255, 255)",
                                borderColor: "rgba(77, 78, 82, 0.7)",

                            },
                            '& .MuiDataGrid-overlayWrapper': {
                                pointerEvents: 'none'
                            },
                            '& .MuiLinearProgress-root':{
                                backgroundColor:'green',
                                pointerEvents: 'none'
                            },
                            [`& .${gridClasses.menuIcon}`]: { // Totally overwrite the default styling for the 3-dot-menu button. Ensure that we set width
                            visibility: 'visible',
                            color: '#d2d2d2', // Change the column header background to black
                            width: 'auto',
                            },    
                            [`& .${gridClasses.columnSeparator}`]: { // Totally overwrite the default styling for the column separator. Ensure that we set width
                                visibility: 'visible',
                                color: '#d2d2d2', // Change the column header background to black
                                width: 'auto',
                            }
                        }}

                        slots={{
                            loadingOverlay: determinateLoadingOverlay,
                            booleanCellTrueIcon: GreenCheck,
                            booleanCellFalseIcon: RedCross
                        }}
                        apiRef={apiRef}
                        columns={exploreColumns}
                        rows={
                            showSelectedOnly
                            ?
                                exploreRows.filter((shot) => rowSelectionModel.includes(shot.UniqueID))
                            :
                                exploreRows
                        }
                        loading={isLoading}
                        getRowId={(row) => row.UniqueID}
                        checkboxSelection
                        checkboxSelectionVisibleOnly


                        selectionModel={rowSelectionModel}
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel)
                        }}
                        filterModel={filterModel}
                        onFilterModelChange={(newFilterModel) => {
                            if (newFilterModel['items'] !== filterModel['items']) {
                                setFilterModel(newFilterModel)
                            }
                        }}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={handleColumnVisibilityChange}
                        rowHeight={100}

                    />
                    </div>  
                )}

                {tabs.map((tab, index) => (
                    value > 0 && value === index && (
                        value === index && 
                    <ShotDetailPage key={index} clipname={tabs[value]} setIsLoading={setIsLoading} />
                    )
                ))}

                </Box>
            </Box>
            {/* Dialog boxes */}
            <UploadExploreDialog
                open={openUploadDialog}
                setOpen={setOpenUploadDialog}
                anchorEl={uploadRef} 
                filterClips={filterClips}
            />
            {/* <SelectEdlDialog
                open={openEdlMenu}
                setOpen={setOpenEdlMenu}
                anchorEl={edlAnchorEl}
                filterClips={filterClips}
            /> */}
            <SelectExportsDialog
                open={openExportMenu}
                setOpen={setOpenExportMenu}
                anchorEl={exportAnchorEl}
                downloadExport={downloadExport}
            /> 
            <ExportCdlDialog
                open={openCdlMenu}
                setOpen={setOpenCdlMenu}
                anchorEl={CdlAnchorEl}
                downloadExport={downloadCDL}
                generateCDLEDL={generateCDLEDL}
                toggleCdlUploadMenu={toggleCdlUploadMenu}
                setCdlUploadSource={setCdlUploadSource}
                selectCount={rowSelectionModel.length}
                type='CDL'
            /> 
            <UploadEDLForCDLInjectDialog
                open={openCdlUploadMenu}
                setOpen={setOpenCdlUploadMenu}
                anchorEl={CdlUploadAnchorEl}  
                cdlUploadSource={cdlUploadSource}          
            />

            {/* END Dialog boxes */}
        </React.Fragment>
    )
}